import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, FileCheck, Users, CalendarCheck, ArrowUpRight, ShieldCheck, MapPin } from "lucide-react";
import { Reveal, SectionHeading, Eyebrow, EstimateButton, ProjectsButton } from "@/components/Shared";
import { SERVICES, PROJECTS, IMAGES, FAQS, PHONE_DISPLAY, PHONE_TEL } from "@/data/content";
import { LOCATIONS } from "@/data/locations";
import { useSEO } from "@/hooks/useSEO";

const Hero = () => (
  <section className="border-b border-obsidian/10" data-testid="hero-section">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <Eyebrow>Home Renovation Contractor · GTA & Kitchener-Waterloo</Eyebrow>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-obsidian" data-testid="hero-headline">
            Quality renovation services, <em className="italic text-teak">built around your home.</em>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-stone-600 font-light leading-relaxed">
            Kitchens, bathrooms, basements, flooring and full-home transformations across the GTA & Kitchener-Waterloo — fixed-price quotes, one accountable crew.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <EstimateButton testId="hero-estimate-button" />
            <ProjectsButton testId="hero-projects-button" />
          </div>
          <p className="mt-8 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-stonemuted">
            <ShieldCheck size={15} className="text-teak" /> Licensed & Insured · 2-Year Workmanship Warranty
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="lg:col-span-5">
        <div className="overflow-hidden border border-obsidian/15 aspect-[4/5]">
          <img
            src={IMAGES.K1}
            alt="Kitchen renovation by MTC Home Renovation, serving the GTA and Kitchener-Waterloo"
            className="w-full h-full object-cover"
            data-testid="hero-image"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const ServicesPreview = () => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? SERVICES : SERVICES.slice(0, 3);
  return (
    <section className="py-20 sm:py-28 border-b border-obsidian/10" data-testid="services-preview">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="What We Do" title="Renovation services for every part of your home." />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((s, i) => (
              <motion.div
                layout
                key={s.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: i >= 3 ? (i - 3) * 0.08 : 0 }}
              >
                <Link to="/services" data-testid={`home-service-${s.id}`} className="group block">
                  <div className="overflow-hidden border border-obsidian/15 aspect-[4/3]">
                    <img
                      src={s.image}
                      alt={`${s.title} — home renovation GTA & Kitchener-Waterloo`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-medium text-obsidian group-hover:text-teak transition-colors duration-300">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{s.desc}</p>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        <div className="mt-10 text-center">
          <button
            data-testid="services-show-more"
            onClick={() => setShowAll(!showAll)}
            className="group inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border border-obsidian/25 text-obsidian hover:bg-obsidian hover:text-bone transition-colors duration-300"
          >
            {showAll ? "Show less" : `Show all ${SERVICES.length} services`}
            <ChevronDown size={15} className={`transition-transform duration-300 ${showAll ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
    </section>
  );
};

const ProjectsPreview = () => (
  <section className="py-20 sm:py-28 border-b border-obsidian/10 bg-parchment/60" data-testid="projects-preview">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading eyebrow="Recent Work" title="A portfolio we're proud of." />
        <Link to="/projects" data-testid="projects-view-all-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-obsidian hover:text-teak transition-colors">
          View all projects <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {PROJECTS.slice(0, 3).map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link to="/projects" data-testid={`home-project-${p.id}`} className="group block">
              <div className="overflow-hidden border border-obsidian/15 aspect-[4/3]">
                <img src={p.image} alt={`${p.title} — ${p.category} renovation in ${p.area}, Ontario`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-teak">{p.category} · {p.area}</p>
              <h3 className="mt-1 font-display text-xl font-medium text-obsidian">{p.title}</h3>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WHY = [
  { icon: FileCheck, title: "Transparent estimates", text: "A written, fixed-price quote before work begins. No surprises." },
  { icon: Users, title: "One accountable crew", text: "Our own team, start to finish — clear communication at every step." },
  { icon: CalendarCheck, title: "Reliable scheduling", text: "A realistic timeline in writing, and a clean site every day." },
];

const Why = () => (
  <section className="py-20 sm:py-28 border-b border-obsidian/10" data-testid="why-section">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <SectionHeading eyebrow="Why MTC" title="A renovation experience built on trust." />
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.1}>
            <w.icon size={22} className="text-teak" />
            <h3 className="mt-4 font-display text-xl font-medium text-obsidian">{w.title}</h3>
            <p className="mt-2 text-sm text-stone-600 leading-relaxed">{w.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AreasWeServe = () => (
  <section className="py-20 sm:py-28 border-b border-obsidian/10 bg-parchment/60" data-testid="areas-section" id="areas">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <SectionHeading eyebrow="Service Areas" title="Serving the GTA & Kitchener-Waterloo." />
      <p className="mt-4 max-w-2xl text-sm text-stone-600 leading-relaxed">
        Local crews, local knowledge. Choose your city to see the renovations we deliver there.
      </p>
      <div className="mt-10 flex flex-wrap gap-2.5">
        {LOCATIONS.map((l) => (
          <Link
            key={l.slug}
            to={`/${l.slug}`}
            data-testid={`area-link-${l.slug}`}
            className="flex items-center gap-1.5 border border-obsidian/15 bg-bone px-4 py-2.5 text-sm font-medium text-obsidian hover:bg-obsidian hover:text-bone transition-colors duration-300"
          >
            <MapPin size={13} className="text-teak" /> {l.name}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 sm:py-28 border-b border-obsidian/10" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow="Common Questions" title="Everything you need to know." />
        <div className="mt-12 border-t border-obsidian/10">
          {FAQS.map((f, i) => (
            <div key={i} className="border-b border-obsidian/10">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 py-5 text-left"
              >
                <span className="font-display text-lg font-medium text-obsidian">{f.q}</span>
                <ChevronDown size={18} className={`shrink-0 text-teak transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-5 text-sm text-stone-600 leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTABanner = () => (
  <section className="bg-obsidian text-bone" data-testid="cta-banner">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
      <div>
        <Eyebrow dark>Book a Free Estimate · GTA & Kitchener-Waterloo</Eyebrow>
        <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight">
          Ready to renovate? Let's talk.
        </h2>
      </div>
      <div className="flex flex-wrap items-center gap-5">
        <EstimateButton testId="banner-estimate-button" />
        <a href={PHONE_TEL} data-testid="banner-call-link" className="text-sm font-semibold text-bone/80 hover:text-bone transition-colors">
          or call {PHONE_DISPLAY}
        </a>
      </div>
    </div>
  </section>
);

export default function Home() {
  useSEO(
    "MTC Home Renovation | Home Renovation Contractor in the GTA & Kitchener-Waterloo",
    "MTC Home Renovation is a professional home renovation company serving the GTA and Kitchener-Waterloo. Kitchen, bathroom and basement renovations, flooring and complete home renovations. Free estimates: (519) 760-0444."
  );
  return (
    <>
      <Hero />
      <ServicesPreview />
      <ProjectsPreview />
      <Why />
      <AreasWeServe />
      <Faq />
      <CTABanner />
    </>
  );
}
